import { useState, useRef, useEffect } from 'react';
import { Check, X } from 'lucide-react';

// Inline editable field - click to edit, blur to save
export function InlineEdit({ value, onSave, type = 'text', placeholder = '', className = '', displayClassName = '', multiline = false, forceEdit = false, onEditEnd = null, readOnlyDisplay = false }) {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (forceEdit) setEditing(true);
  }, [forceEdit]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (!multiline) inputRef.current.select();
    }
  }, [editing, multiline]);

  useEffect(() => {
    setTempValue(value || '');
  }, [value]);

  const handleSave = () => {
    setEditing(false);
    if (tempValue !== value) onSave(tempValue);
    onEditEnd?.();
  };

  const handleCancel = () => {
    setEditing(false);
    setTempValue(value || '');
    onEditEnd?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) handleSave();
    if (e.key === 'Enter' && multiline && e.metaKey) handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  if (editing) {
    return (
      <span className="inline-flex items-start gap-1 w-full">
        {multiline ? (
          <textarea
            ref={inputRef}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={3}
            className={`bg-white/[0.05] border border-[#6B35B8]/50 rounded px-1.5 py-0.5 text-[#f0f0f5] outline-none focus:border-[#6B35B8] resize-none w-full ${className}`}
          />
        ) : (
          <input
            ref={inputRef}
            type={type}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`bg-white/[0.05] border border-[#6B35B8]/50 rounded px-1.5 py-0.5 text-[#f0f0f5] outline-none focus:border-[#6B35B8] ${className}`}
          />
        )}
        <span className="flex flex-col gap-0.5 shrink-0">
          <button onClick={handleSave} className="text-emerald-400 hover:text-emerald-300 p-0.5">
            <Check size={12} />
          </button>
          <button onClick={handleCancel} className="text-red-400 hover:text-red-300 p-0.5">
            <X size={12} />
          </button>
        </span>
      </span>
    );
  }

  if (readOnlyDisplay) {
    return (
      <span className={displayClassName}>
        {value || <span className="opacity-50 italic">{placeholder || ''}</span>}
      </span>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className={`cursor-pointer hover:bg-[#6B35B8]/10 hover:text-[#9B6DD4] rounded px-1 -mx-1 transition-colors ${displayClassName}`}
      title="Click to edit"
    >
      {value || <span className="opacity-50 italic">{placeholder || 'Click to add'}</span>}
    </span>
  );
}

// Inline select for status
export function InlineSelect({ value, options, onSave, className = '' }) {
  const [editing, setEditing] = useState(false);
  const selectRef = useRef(null);

  useEffect(() => {
    if (editing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [editing]);

  const handleChange = (newValue) => {
    setEditing(false);
    if (newValue !== value) {
      onSave(newValue);
    }
  };

  if (editing) {
    return (
      <select
        ref={selectRef}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => setEditing(false)}
        className={`bg-white/[0.05] border border-[#6B35B8]/50 rounded px-1.5 py-0.5 text-xs text-[#f0f0f5] outline-none focus:border-[#6B35B8] ${className}`}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }

  const currentOption = options.find(o => o.value === value);
  return (
    <span 
      onClick={() => setEditing(true)}
      className={`cursor-pointer hover:opacity-80 transition-opacity ${className}`}
      title="Click to change"
    >
      {currentOption?.label || value}
    </span>
  );
}
